/*
 * AdminCoverage — Phase CC-1
 * Single page exposing 5 previously-dark surfaces as tabs:
 *   • RFQ Oversight
 *   • Plaid Links
 *   • Shopify Stores
 *   • Card-Reveal Audit
 *   • Seller Credit Offers
 */
import { useEffect, useState, useCallback } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import {
  Eye, ShoppingCart, Building2, Link as LinkIcon, FileQuestion,
  Loader2, AlertTriangle, ShieldOff, Repeat,
} from "lucide-react";


const TABS = [
  { id: "rfq",      label: "RFQ",            icon: FileQuestion },
  { id: "plaid",    label: "Plaid Links",    icon: LinkIcon },
  { id: "shopify",  label: "Shopify Stores", icon: Building2 },
  { id: "reveals",  label: "Card Reveals",   icon: Eye },
  { id: "offers",   label: "Credit Offers",  icon: ShoppingCart },
];


export default function AdminCoverage() {
  const [tab, setTab] = useState("rfq");

  return (
    <div className="space-y-5 max-w-7xl" data-testid="admin-coverage">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">Coverage Console</h1>
        <p className="text-[12.5px] text-foreground/55">
          Surfaces that were previously written to by buyers/sellers without admin visibility.
        </p>
      </header>

      <div className="flex gap-1 border-b overflow-x-auto" data-testid="coverage-tabs">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              data-testid={`coverage-tab-${t.id}`}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm border-b-2 whitespace-nowrap ${
                tab === t.id ? "border-foreground text-foreground"
                              : "border-transparent text-foreground/55 hover:text-foreground/80"
              }`}>
              <Icon className="w-3.5 h-3.5"/>{t.label}
            </button>
          );
        })}
      </div>

      {tab === "rfq"     && <RfqTab/>}
      {tab === "plaid"   && <PlaidTab/>}
      {tab === "shopify" && <ShopifyTab/>}
      {tab === "reveals" && <RevealsTab/>}
      {tab === "offers"  && <OffersTab/>}
    </div>
  );
}


/* ────────────────────────────────────────────────────────────────────
 * RFQ
 * ──────────────────────────────────────────────────────────────────── */
function RfqTab() {
  const [data, setData] = useState({ rows: [], total: 0, by_status: {} });
  const [busy, setBusy] = useState(false);
  const [filterStatus, setFilterStatus] = useState("");

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set("status", filterStatus);
      params.set("limit", "100");
      const r = await api.get(`/admin/rfq?${params}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load RFQs");
    } finally { setBusy(false); }
  }, [filterStatus]);

  useEffect(() => { load(); }, [load]);

  const close = async (rid) => {
    const reason = window.prompt("Reason for force-closing this RFQ? (min 3 chars · audit-logged)");
    if (!reason || reason.trim().length < 3) {
      if (reason !== null) toast.error("Reason must be ≥ 3 chars");
      return;
    }
    try {
      await api.post(`/admin/rfq/${rid}/force-close`, { reason: reason.trim() });
      toast.success("RFQ force-closed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
          className="h-9 px-2 rounded-md border bg-background text-[13px]"
          data-testid="rfq-status-filter">
          <option value="">All statuses</option>
          {Object.keys(data.by_status || {}).map(s => (
            <option key={s} value={s}>{s} ({data.by_status[s]})</option>
          ))}
        </select>
        <Button variant="outline" onClick={load} disabled={busy} data-testid="rfq-refresh">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : "Refresh"}
        </Button>
        <span className="text-[11.5px] text-foreground/55 ml-auto">Total: {data.total}</span>
      </div>
      <div className="rounded-xl border overflow-hidden">
        <table className="w-full text-[12.5px]" data-testid="coverage-rfq-table">
          <thead className="bg-foreground/5 text-left text-[11px] uppercase tracking-wider text-foreground/55">
            <tr>
              <th className="px-3 py-2">Title</th>
              <th className="px-3 py-2">Buyer</th>
              <th className="px-3 py-2">Target qty</th>
              <th className="px-3 py-2">Target price</th>
              <th className="px-3 py-2">Quotes</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {data.rows.map(r => (
              <tr key={r.id} data-testid={`rfq-row-${r.id}`}>
                <td className="px-3 py-2 font-medium">{r.title || "—"}</td>
                <td className="px-3 py-2 text-foreground/65">{r.buyer_company || r.buyer_name || r.buyer_id}</td>
                <td className="px-3 py-2">{r.target_qty ?? "—"}</td>
                <td className="px-3 py-2 mono">{r.target_price ? `$${r.target_price}` : "—"}</td>
                <td className="px-3 py-2">{(r.quotes || []).length}</td>
                <td className="px-3 py-2">
                  <span className="chip text-[10.5px] bg-foreground/5 border-foreground/10">{r.status || "—"}</span>
                </td>
                <td className="px-3 py-2 text-right">
                  {!["closed", "force_closed", "expired"].includes(r.status) && (
                    <Button variant="ghost" size="sm" onClick={() => close(r.id)}
                      data-testid={`rfq-close-${r.id}`}>Force-close</Button>
                  )}
                </td>
              </tr>
            ))}
            {data.rows.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-foreground/45">No RFQs</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


/* ────────────────────────────────────────────────────────────────────
 * Plaid
 * ──────────────────────────────────────────────────────────────────── */
function PlaidTab() {
  const [data, setData] = useState({ rows: [], total: 0 });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.get(`/admin/integrations/plaid/links?limit=100`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load Plaid links");
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const relink = async (id) => {
    const reason = window.prompt("Reason for forcing Plaid relink? (min 3 chars · audit-logged)");
    if (!reason || reason.trim().length < 3) {
      if (reason !== null) toast.error("Reason must be ≥ 3 chars");
      return;
    }
    try {
      await api.post(`/admin/integrations/plaid/links/${id}/force-relink`,
                      { reason: reason.trim() });
      toast.success("Plaid item marked as needs_relink");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={load} disabled={busy} data-testid="plaid-refresh">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : "Refresh"}
        </Button>
        <span className="text-[11.5px] text-foreground/55">Total: {data.total}</span>
      </div>
      <div className="rounded-xl border overflow-hidden">
        <table className="w-full text-[12.5px]" data-testid="coverage-plaid-table">
          <thead className="bg-foreground/5 text-left text-[11px] uppercase tracking-wider text-foreground/55">
            <tr>
              <th className="px-3 py-2">Buyer</th>
              <th className="px-3 py-2">Institution</th>
              <th className="px-3 py-2">Item ID</th>
              <th className="px-3 py-2">Accounts</th>
              <th className="px-3 py-2">Connected</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {data.rows.map(r => (
              <tr key={r.id || r.item_id} data-testid={`plaid-row-${r.id || r.item_id}`}>
                <td className="px-3 py-2">{r.buyer?.email || r.user_id || "—"}</td>
                <td className="px-3 py-2 font-medium">{r.institution_name || "—"}</td>
                <td className="px-3 py-2 mono text-[11px]">{(r.item_id || "").slice(0, 16) || "—"}</td>
                <td className="px-3 py-2">{r.accounts_count ?? (r.accounts?.length || 0)}</td>
                <td className="px-3 py-2 mono text-[11px]">
                  {r.connected_at ? new Date(r.connected_at).toLocaleDateString() : "—"}
                </td>
                <td className="px-3 py-2">
                  <span className={`chip text-[10.5px] ${
                    r.needs_relink ? "bg-amber-50 border-amber-200 text-amber-800"
                                    : "bg-emerald-50 border-emerald-200 text-emerald-800"
                  }`}>{r.needs_relink ? "Needs relink" : (r.status || "active")}</span>
                </td>
                <td className="px-3 py-2 text-right">
                  <Button variant="ghost" size="sm"
                    onClick={() => relink(r.id || r.item_id)}
                    data-testid={`plaid-relink-${r.id || r.item_id}`}>
                    <Repeat className="w-3 h-3 mr-1"/>Force relink
                  </Button>
                </td>
              </tr>
            ))}
            {data.rows.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-foreground/45">No Plaid links</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


/* ────────────────────────────────────────────────────────────────────
 * Shopify
 * ──────────────────────────────────────────────────────────────────── */
function ShopifyTab() {
  const [data, setData] = useState({ rows: [], total: 0, by_status: {} });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.get(`/admin/integrations/shopify/stores?limit=100`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load Shopify stores");
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const disconnect = async (id) => {
    const reason = window.prompt("Reason for force-disconnecting? (min 3 chars · audit-logged)");
    if (!reason || reason.trim().length < 3) {
      if (reason !== null) toast.error("Reason must be ≥ 3 chars");
      return;
    }
    try {
      await api.post(`/admin/integrations/shopify/stores/${id}/force-disconnect`,
                      { reason: reason.trim() });
      toast.success("Store disconnected");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex gap-2 flex-wrap">
          {Object.entries(data.by_status || {}).map(([k, v]) => (
            <span key={k} className="chip text-[10.5px] bg-foreground/5 border-foreground/10">
              {k}: {v}
            </span>
          ))}
        </div>
        <Button variant="outline" onClick={load} disabled={busy} data-testid="shopify-refresh">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : "Refresh"}
        </Button>
      </div>
      <div className="rounded-xl border overflow-hidden">
        <table className="w-full text-[12.5px]" data-testid="coverage-shopify-table">
          <thead className="bg-foreground/5 text-left text-[11px] uppercase tracking-wider text-foreground/55">
            <tr>
              <th className="px-3 py-2">Seller</th>
              <th className="px-3 py-2">Store URL</th>
              <th className="px-3 py-2">Platform</th>
              <th className="px-3 py-2">Synced products</th>
              <th className="px-3 py-2">Last sync</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {data.rows.map(r => (
              <tr key={r.id} data-testid={`shopify-row-${r.id}`}>
                <td className="px-3 py-2">{r.seller?.email || r.user_id || "—"}</td>
                <td className="px-3 py-2 mono text-[11px]">{r.store_url || "—"}</td>
                <td className="px-3 py-2">{r.platform || "shopify"}</td>
                <td className="px-3 py-2">{r.products_synced ?? "—"}</td>
                <td className="px-3 py-2 mono text-[11px]">
                  {r.last_synced_at ? new Date(r.last_synced_at).toLocaleDateString() : "—"}
                </td>
                <td className="px-3 py-2">
                  <span className={`chip text-[10.5px] ${
                    r.status === "disconnected" ? "bg-rose-50 border-rose-200 text-rose-800"
                      : r.status === "active" ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                                                : "bg-foreground/5 border-foreground/10"
                  }`}>{r.status || "—"}</span>
                </td>
                <td className="px-3 py-2 text-right">
                  {r.status !== "disconnected" && (
                    <Button variant="ghost" size="sm" onClick={() => disconnect(r.id)}
                      data-testid={`shopify-disconnect-${r.id}`}>
                      <ShieldOff className="w-3 h-3 mr-1"/>Disconnect
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {data.rows.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-foreground/45">No Shopify stores</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


/* ────────────────────────────────────────────────────────────────────
 * Card / payment-method reveals (fraud audit)
 * ──────────────────────────────────────────────────────────────────── */
function RevealsTab() {
  const [data, setData] = useState({ rows: [], total: 0, distinct_users: 0, failed: 0 });
  const [busy, setBusy] = useState(false);
  const [days, setDays] = useState(30);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.get(`/admin/payment-method-reveals?since_days=${days}&limit=200`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load reveals");
    } finally { setBusy(false); }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
        <Kpi label="Reveals" value={data.total ?? 0}/>
        <Kpi label="Distinct users" value={data.distinct_users ?? 0}/>
        <Kpi label="Failed" value={data.failed ?? 0}
              tone={data.failed > 0 ? "rose" : "neutral"}
              testid="reveals-failed-tile"/>
        <Kpi label="Window" value={`${data.since_days || days}d`}/>
      </div>
      <div className="flex items-center gap-2">
        <Label htmlFor="rev-days" className="text-[12px]">Window (days)</Label>
        <Input id="rev-days" type="number" value={days}
          onChange={(e) => setDays(Number(e.target.value) || 30)}
          className="h-9 w-28" data-testid="reveals-days"/>
        <Button variant="outline" onClick={load} disabled={busy} data-testid="reveals-refresh">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : "Refresh"}
        </Button>
      </div>
      <div className="rounded-xl border overflow-hidden">
        <table className="w-full text-[12.5px]" data-testid="coverage-reveals-table">
          <thead className="bg-foreground/5 text-left text-[11px] uppercase tracking-wider text-foreground/55">
            <tr>
              <th className="px-3 py-2">When</th>
              <th className="px-3 py-2">User</th>
              <th className="px-3 py-2">Method ID</th>
              <th className="px-3 py-2">Outcome</th>
              <th className="px-3 py-2">IP</th>
              <th className="px-3 py-2">UA</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {data.rows.map(r => (
              <tr key={r.id} data-testid={`reveals-row-${r.id}`}>
                <td className="px-3 py-2 mono text-[11px]">
                  {r.at ? new Date(r.at).toLocaleString() : "—"}
                </td>
                <td className="px-3 py-2">{r.user?.email || r.user_id || "—"}</td>
                <td className="px-3 py-2 mono text-[11px]">{(r.method_id || "").slice(0, 14)}…</td>
                <td className="px-3 py-2">
                  <span className={`chip text-[10.5px] ${
                    r.outcome === "ok" ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                                          : "bg-rose-50 border-rose-200 text-rose-800"
                  }`}>{r.outcome || "?"}</span>
                </td>
                <td className="px-3 py-2 mono text-[11px]">{r.ip || "—"}</td>
                <td className="px-3 py-2 text-foreground/55 text-[11px] max-w-[200px] truncate">
                  {r.user_agent || "—"}
                </td>
              </tr>
            ))}
            {data.rows.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-foreground/45">No reveal events</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


/* ────────────────────────────────────────────────────────────────────
 * Seller credit offers
 * ──────────────────────────────────────────────────────────────────── */
function OffersTab() {
  const [data, setData] = useState({ rows: [], total: 0, by_status: {} });
  const [busy, setBusy] = useState(false);
  const [filterStatus, setFilterStatus] = useState("");

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set("status", filterStatus);
      params.set("limit", "100");
      const r = await api.get(`/admin/seller-credit-offers?${params}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load credit offers");
    } finally { setBusy(false); }
  }, [filterStatus]);

  useEffect(() => { load(); }, [load]);

  const revoke = async (oid, limit) => {
    const reason = window.prompt(`Reason for revoking this $${limit || 0} offer? (min 3 chars · audit-logged)`);
    if (!reason || reason.trim().length < 3) {
      if (reason !== null) toast.error("Reason must be ≥ 3 chars");
      return;
    }
    try {
      await api.post(`/admin/seller-credit-offers/${oid}/revoke`, { reason: reason.trim() });
      toast.success("Offer revoked");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
          className="h-9 px-2 rounded-md border bg-background text-[13px]"
          data-testid="offers-status-filter">
          <option value="">All statuses</option>
          {Object.keys(data.by_status || {}).map(s => (
            <option key={s} value={s}>{s} ({data.by_status[s]})</option>
          ))}
        </select>
        <Button variant="outline" onClick={load} disabled={busy} data-testid="offers-refresh">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : "Refresh"}
        </Button>
        <span className="text-[11.5px] text-foreground/55 ml-auto">Total: {data.total}</span>
      </div>
      <div className="rounded-xl border overflow-hidden">
        <table className="w-full text-[12.5px]" data-testid="coverage-offers-table">
          <thead className="bg-foreground/5 text-left text-[11px] uppercase tracking-wider text-foreground/55">
            <tr>
              <th className="px-3 py-2">Seller</th>
              <th className="px-3 py-2">Buyer</th>
              <th className="px-3 py-2 text-right">Limit</th>
              <th className="px-3 py-2">Terms</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {data.rows.map(r => (
              <tr key={r.id} data-testid={`offers-row-${r.id}`}>
                <td className="px-3 py-2 font-medium">{r.seller_name || r.seller_id}</td>
                <td className="px-3 py-2 text-foreground/65">{r.buyer_name || r.buyer_email || r.buyer_id}</td>
                <td className="px-3 py-2 text-right mono">${(r.limit ?? 0).toLocaleString()}</td>
                <td className="px-3 py-2">{r.terms || "—"}</td>
                <td className="px-3 py-2">
                  <span className={`chip text-[10.5px] ${
                    r.status === "revoked" ? "bg-rose-50 border-rose-200 text-rose-800"
                      : r.status === "accepted" ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                                                  : "bg-foreground/5 border-foreground/10"
                  }`}>{r.status || "—"}</span>
                </td>
                <td className="px-3 py-2 text-right">
                  {r.status !== "revoked" && (
                    <Button variant="ghost" size="sm" onClick={() => revoke(r.id, r.limit)}
                      data-testid={`offers-revoke-${r.id}`}>
                      <AlertTriangle className="w-3 h-3 mr-1"/>Revoke
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {data.rows.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-foreground/45">No offers</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function Kpi({ label, value, tone = "neutral", testid }) {
  const tones = {
    neutral: "bg-foreground/3 border-foreground/10",
    emerald: "bg-emerald-50 border-emerald-200",
    amber:   "bg-amber-50 border-amber-200",
    rose:    "bg-rose-50 border-rose-200",
  };
  return (
    <div className={`rounded-xl border p-3 ${tones[tone]}`} data-testid={testid}>
      <div className="text-[11px] text-foreground/55 uppercase tracking-wider">{label}</div>
      <div className="text-2xl font-semibold mt-1">{value?.toLocaleString?.() ?? value}</div>
    </div>
  );
}
